import Table, { ColumnProps } from "antd/es/table";
import { useBinDTOContext } from "src/queries/BinDTOContext"
import { formatNumber } from "../../HeaterControls";
import _, { bind } from "lodash";
import React from "react";
import { Card, Flex, Popover, Tag, Typography } from "antd";
import BinDTO from "src/models/BinDTO";
import TemperatureSensorEnum from "src/consts/TemperatureSensorEnum";
import { DataTransformer } from "./dataTransformation";
import { SensorNumberWidth, SensorNameWidth } from "../shared";
import { sensorIgnoredText } from "./shared";
import { useQuery } from "@tanstack/react-query";

const { Text, Title } = Typography;

export const TemperatureCableTable = (props: {}) => {

    const binDTOContext = useBinDTOContext();
    const normalizedData = useTranformTemperatureSensorData(binDTOContext.binDTO!)
    if (binDTOContext.binDTO == null) {
        console.log("bindto is null in temperatureCableTable");
        return null;
    }

    const columns: ColumnProps<SensorDataType[0]>[] = [];
    columns.push({
        width: SensorNumberWidth,
        align: "center",
        dataIndex: ["number"],
        render(value, record, index) {
            return <span style={{ fontWeight: "bold" }}>{value}</span>
        },
    })

    for (const [cableIndex, cable] of (normalizedData.cableIds ?? [])?.entries()) {
        columns.push({
            title: cable.shortDisplayName,
            width: SensorNameWidth,
            align: "center",
            dataIndex: ["sensors", cableIndex, "temperature"],
            render(value, record, tableIndex) {
                const sensorId = record.sensors[cableIndex]?.sensorId;
                if (binDTOContext.ignoredSensors?.includes(sensorId!)) {
                    return sensorIgnoredText;
                }

                return <span>{formatNumber(value, { decimalPlaces: 0, suffix: "°", filler: "" })}</span>;
            },
        });
    }

    if (normalizedData.cableIds.length === 0) {
        return null;
    }

    const binDto = binDTOContext.binDTO;
    let offsetStr = "";
    let offset : number | null | undefined = 0.0;
    if(binDto?.binSettings?.tempSensorDataSource === TemperatureSensorEnum.Thermocouple) {
        offset = binDto?.binSettings?.tempCableOffsetF;
    }else {
        offset = binDto?.binSettings?.moistureCableTempOffsetF;
    }

    offsetStr = `${formatNumber(offset, { decimalPlaces: 1, suffix: "°F", filler: "" })}`;

    return <Card styles={{ body: { padding: 0, overflow: "auto" } }} size="small" 
    title={
    <>
        <Text>Temperature Cable Readings (°F)</Text> 
        {
            offset != null && offset != 0.0 && 
            <Popover
            title={'Temperature Offset'}
            content={'This is the offset applied to the temperature readings from the cables.'}
            >
                <Tag color={'gray'}>{offsetStr}</Tag>
            </Popover>
            
        }
    </>
    }
    >
        <div style={{ minWidth: 128 }}>
            <Table tableLayout="fixed" size="small" pagination={false} dataSource={normalizedData.sensorData} columns={columns}
                scroll={{ x: "max-content" }}
                style={{ tableLayout: "fixed", overflowX: 'auto', width: "max-content", minWidth: "max-content" }}
            />
        </div>
    </Card>
}

export type SensorDataType = Array<{ number: number | null, sensors: Array<{ cableId: string | null, sensorId: string | null, temperature: number | null, mc?: number | null,
    rh?: number | null,
    ah?: number | null,
    dp?: number | null,
}> }>;
export interface temperatureDataTranposed {
    cableIds: Array<{ cableId: string | null, shortDisplayName: string | null }>,
    sensorData: SensorDataType,
}
const useTranformTemperatureSensorData = (bindto: BinDTO): temperatureDataTranposed => {

    const dataTransformer = new DataTransformer();
    if (bindto == null) {
        return {
            cableIds: dataTransformer.cableIds,
            sensorData: dataTransformer.sensorLayers,
        };
    }

    if (bindto.temperatureSensorsType === TemperatureSensorEnum.Thermocouple || bindto.temperatureSensorsType === TemperatureSensorEnum.DigitalTemp) {
        for (let [cableIndex, cable] of bindto.temperatureCables?.entries() ?? []) {
            dataTransformer.populateCableInfo(cable);
            for (let [sensorIndex, sensor] of cable?.thermocouples?.entries() ?? []) {
                dataTransformer.populateSensor(cable, sensor);
            }
        }
    }

    else if (bindto.temperatureSensorsType === TemperatureSensorEnum.Opi) {
        for (let [cableIndex, cable] of bindto.opiMoistureCables?.entries() ?? []) {
            dataTransformer.populateCableInfo(cable);
            for (let [sensorIndex, sensor] of cable?.rhtStates?.entries() ?? []) {
                dataTransformer.populateSensor(cable, sensor);
            }
        }
    }

    else if (bindto.temperatureSensorsType === TemperatureSensorEnum.BinSense) {
        for (let [cableIndex, cable] of bindto.binSenseMoistureCables?.entries() ?? []) {
            dataTransformer.populateCableInfo(cable);
            for (let [sensorIndex, sensor] of cable?.rhtStates?.entries() ?? []) {
                dataTransformer.populateSensor(cable, sensor);
            }
        }
    }

    else if (bindto.temperatureSensorsType === TemperatureSensorEnum.PowerCast) {
        for (let [cableIndex, cable] of bindto.moistureCables_RF?.entries() ?? []) {
            dataTransformer.populateCableInfo(cable);
            for (let [sensorIndex, sensor] of cable?.wirelessStates?.entries() ?? []) {
                dataTransformer.populateSensor(cable, sensor);
            }
        }
    }

    dataTransformer.sensorLayers.reverse();

    return {
        cableIds: dataTransformer.cableIds,
        sensorData: dataTransformer.sensorLayers,
    };

}